<script setup lang="ts">
import { onBeforeMount, onMounted, onUnmounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import RudderStack from "@/lib/rudderstack";
import useTrackerStore from "@/stores/trackers";
import { useAppVersion } from "@/composables/useAppVersion";
import { Toaster } from "./components/ui/sonner";
import AppUpdateBanner from "./components/app/AppUpdateBanner.vue";
import { ldInit, useLDFlag } from "launchdarkly-vue-client-sdk";
import useFeatureStore from "./stores/features";
import { useTheme } from "./composables/useTheme";
import { RudderAnalytics } from "@rudderstack/analytics-js";
import { VueQueryDevtools } from "@tanstack/vue-query-devtools";
// Only import VueQueryDevtools in development environments
const isDev = import.meta.env.DEV;

const [ldReady] = ldInit({
  clientSideID: import.meta.env.VITE_LAUNCH_DARKLY_CLIENT_ID,
});

useFeatureStore();

const rudderstack = new RudderStack(new RudderAnalytics());
const trackers = useTrackerStore();
const router = useRouter();
const route = useRoute();
const { checkForNewerVersion } = useAppVersion();
rudderstack.init();
rudderstack.registerRouter(router);
trackers.rudderstack = rudderstack;

const { setColorMode } = useTheme();

const hasDarkMode = useLDFlag("dark-mode");

watch(hasDarkMode, (next) => {
  if (next && next === true) {
    setColorMode("auto");
  } else {
    setColorMode("light");
  }
});

onBeforeMount(async () => {
  await router.isReady();
  if (import.meta.env.VITE_VERSION_POLLING_INTERVAL) {
    setInterval(checkForNewerVersion, import.meta.env.VITE_VERSION_POLLING_INTERVAL);
  }
  navigator.serviceWorker.getRegistrations().then(function (registrations) {
    for (let registration of registrations) {
      registration.unregister();
    }
  });
});

function handleAuthError() {
  localStorage.setItem("toPath", route.fullPath);
  router.push({ name: "login" });
}

onMounted(() => {
  window.addEventListener("auth:unauthorized", handleAuthError);
});

onUnmounted(() => {
  window.removeEventListener("auth:unauthorized", handleAuthError);
});
</script>

<template>
  <div>
    <AppUpdateBanner />
    <div id="modal-container"></div>
    <router-view v-if="ldReady" />
    <Toaster rich-colors />
  </div>
  <VueQueryDevtools v-if="isDev" />
</template>
